import React, { useState } from "react";
import { DatePicker, Space, Spin } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import StepHeader from "../StepHeader";
import { usePermissions } from "../../../PermissionsProvider";
import { EvaluationService } from "../../../services/EvaluationService";

const { RangePicker } = DatePicker;

interface SelectDatesProps {
  startDate?: Date;
  endDate?: Date;
  onChange: (startDate: Date | undefined, endDate: Date | undefined) => void;
  applicationId?: number;
  intentId?: number;
}

interface EvaluationRunInfo {
  total_requests: number;
  // add other fields as needed
}

const SelectDates: React.FC<SelectDatesProps> = ({
  startDate,
  endDate,
  onChange,
  applicationId,
  intentId,
}) => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [evaluationRunInfo, setEvaluationRunInfo] = useState<EvaluationRunInfo | null>(null);

  const handleChange: RangePickerProps["onChange"] = (dates) => {
    if (dates) {
      const startDateTime = dates[0]?.startOf("day").toDate();
      const endDateTime = dates[1]?.endOf("day").toDate();
      onChange(startDateTime, endDateTime);

      // Fetch evaluation info if we have all required parameters
      if (applicationId && intentId && startDateTime && endDateTime) {
        const payload = {
          application_id: applicationId,
          intent_id: intentId,
          date_start: startDateTime.getTime(),
          date_end: endDateTime.getTime()
        };

        EvaluationService.getEvaluationRunInfo(payload, setLoading, apiKey).subscribe(
          (info: EvaluationRunInfo) => {
            setEvaluationRunInfo(info);
          },
          (error: any) => {
            console.error("Error fetching evaluation run info:", error);
          }
        );
      }
    } else {
      onChange(undefined, undefined);
      setEvaluationRunInfo(null);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <StepHeader
        title="Select Date Range"
        description="Choose the time period for your evaluation"
      />
      
      <Space>
        {loading ? (
          <Spin />
        ) : (
          <RangePicker
            style={{ width: "100%", maxWidth: "500px" }}
            onChange={handleChange}
            value={[
              startDate ? dayjs(startDate) : null,
              endDate ? dayjs(endDate) : null
            ]}
            size="large"
          />
        )}
        {!loading && evaluationRunInfo && (
          <span style={{ marginLeft: "12px" }}>{evaluationRunInfo.total_requests} requests</span>
        )}
      </Space>
    </Space>
  );
};

export default SelectDates;